import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ImagePlus, Loader2, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, imgUrl, apiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";

const EMPTY = { name: "", category: CATEGORIES[0], description: "", location: "", date: "", published: true };

export default function Realizations() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [before, setBefore] = useState(null);
  const [after, setAfter] = useState(null);
  const [extras, setExtras] = useState([]);
  const [saving, setSaving] = useState(false);
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const extrasRef = useRef(null);

  const load = () => api.get("/admin/realizations").then((r) => setItems(r.data)).catch(() => toast.error("Chargement impossible"));
  useEffect(() => { load(); }, []);

  const openForm = (item = null) => {
    setEditing(item);
    setForm(item ? { name: item.name, category: item.category, description: item.description || "", location: item.location || "", date: item.date || "", published: item.published } : EMPTY);
    setBefore(null);
    setAfter(null);
    setExtras([]);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing && (!before || !after)) {
      toast.error("Ajoutez une photo AVANT et une photo APRÈS.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
      if (before) fd.append("before", before);
      if (after) fd.append("after", after);
      extras.forEach((f) => fd.append("extras", f));
      if (editing) await api.put(`/admin/realizations/${editing.id}`, fd);
      else await api.post("/admin/realizations", fd);
      toast.success(editing ? "Réalisation mise à jour" : "Réalisation publiée sur le site");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    const fd = new FormData();
    fd.append("name", item.name);
    fd.append("category", item.category);
    fd.append("description", item.description || "");
    fd.append("location", item.location || "");
    fd.append("date", item.date || "");
    fd.append("published", String(!item.published));
    try {
      await api.put(`/admin/realizations/${item.id}`, fd);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.name} » définitivement ?`)) return;
    try {
      await api.delete(`/admin/realizations/${item.id}`);
      toast.success("Réalisation supprimée");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const dragIndex = useRef(null);

  const reorderDrop = async (target) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null || from === target) return;
    const arr = [...items];
    const [moved] = arr.splice(from, 1);
    arr.splice(target, 0, moved);
    setItems(arr);
    try {
      await api.put("/admin/realizations-order", { ids: arr.map((x) => x.id) });
      toast.success("Ordre enregistré");
    } catch {
      toast.error("Ordre non enregistré");
      load();
    }
  };

  const move = async (index, dir) => {
    const arr = [...items];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setItems(arr);
    try {
      await api.put("/admin/realizations-order", { ids: arr.map((x) => x.id) });
    } catch {
      toast.error("Ordre non enregistré");
      load();
    }
  };

  const inputCls = "w-full rounded-xl border border-line bg-cream px-4 py-2.5 text-sm text-ink placeholder:text-muted2 focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";

  const DropZone = ({ label, file, inputRef, onPick, testid, existing }) => (
    <div>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" className="hidden" data-testid={`${testid}-input`} onChange={(e) => { if (e.target.files[0]) onPick(e.target.files[0]); e.target.value = ""; }} />
      <button type="button" onClick={() => inputRef.current?.click()} data-testid={testid}
        className="w-full aspect-[16/10] rounded-xl border-2 border-dashed border-line bg-cream overflow-hidden grid place-items-center text-sm text-muted2 hover:border-pine transition-colors relative">
        {file ? (
          <img src={URL.createObjectURL(file)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : existing ? (
          <img src={imgUrl(existing)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <span className="flex flex-col items-center gap-2 p-4"><ImagePlus className="h-6 w-6 text-pine" />{label}</span>
        )}
        <span className="absolute bottom-2 left-2 rounded-full bg-ink/70 text-cream text-[10px] font-mono tracking-widest px-2.5 py-1">{label.toUpperCase()}</span>
      </button>
    </div>
  );

  return (
    <div data-testid="admin-realisations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-semibold text-2xl sm:text-3xl text-ink">Réalisations</h1>
          <p className="mt-1 text-sm text-muted2">Les réalisations publiées apparaissent automatiquement dans la section Avant / Après du site.</p>
        </div>
        <button onClick={() => openForm()} data-testid="add-realisation-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream text-sm font-semibold px-5 py-2.5 hover:bg-pine transition-colors">
          <Plus className="h-4 w-4" /> Ajouter une réalisation
        </button>
      </div>

      {!items ? (
        <p className="mt-10 text-sm text-muted2">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-dashed border-line bg-white p-12 text-center text-sm text-muted2" data-testid="realisations-admin-empty">
          Aucune réalisation. Ajoutez votre premier avant / après.
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((r, i) => (
            <article key={r.id} data-testid={`realisation-item-${r.id}`}
              draggable
              onDragStart={() => { dragIndex.current = i; }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => reorderDrop(i)}
              className="rounded-2xl bg-white border border-line overflow-hidden cursor-grab active:cursor-grabbing">
              <div className="grid grid-cols-2 h-36">
                <div className="relative overflow-hidden bg-sand">
                  <img src={imgUrl(r.before)} alt="Avant" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute bottom-1.5 left-1.5 text-[9px] font-mono bg-ink/70 text-cream rounded px-1.5 py-0.5">AVANT</span>
                </div>
                <div className="relative overflow-hidden bg-sand">
                  <img src={imgUrl(r.after)} alt="Après" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute bottom-1.5 right-1.5 text-[9px] font-mono bg-terra text-white rounded px-1.5 py-0.5">APRÈS</span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm text-ink truncate">{r.name}</h3>
                    <p className="text-xs text-muted2 mt-0.5">{r.category}{r.location ? ` — ${r.location}` : ""}</p>
                  </div>
                  {r.is_placeholder && <span className="shrink-0 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold px-2 py-0.5">Exemple</span>}
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-body2">
                    <Switch checked={r.published} onCheckedChange={() => togglePublish(r)} data-testid={`realisation-publish-${r.id}`} />
                    {r.published ? "Publié" : "Masqué"}
                  </label>
                  <div className="flex items-center gap-1">
                    <GripVertical className="h-4 w-4 text-line" aria-hidden="true" />
                    <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Monter" className="p-1.5 rounded-lg hover:bg-sand disabled:opacity-30" data-testid={`realisation-up-${r.id}`}><ArrowUp className="h-4 w-4 text-body2" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label="Descendre" className="p-1.5 rounded-lg hover:bg-sand disabled:opacity-30" data-testid={`realisation-down-${r.id}`}><ArrowDown className="h-4 w-4 text-body2" /></button>
                    <button onClick={() => openForm(r)} aria-label="Modifier" className="p-1.5 rounded-lg hover:bg-sand" data-testid={`realisation-edit-${r.id}`}><Pencil className="h-4 w-4 text-body2" /></button>
                    <button onClick={() => remove(r)} aria-label="Supprimer" className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`realisation-delete-${r.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="realisation-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifier la réalisation" : "Nouvelle réalisation"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-body2 mb-1.5">Type d'intervention</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="realisation-form-category" className={inputCls}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-body2 mb-1.5">Titre du chantier</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex. : Remise en état d'une terrasse" data-testid="realisation-form-name" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-body2 mb-1.5">Localisation</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex. : Caen" data-testid="realisation-form-location" className={inputCls} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <DropZone label="Avant" file={before} inputRef={beforeRef} onPick={setBefore} testid="realisation-form-before" existing={editing?.before} />
              <DropZone label="Après" file={after} inputRef={afterRef} onPick={setAfter} testid="realisation-form-after" existing={editing?.after} />
            </div>

            <div>
              <input ref={extrasRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" data-testid="realisation-form-extras-input" onChange={(e) => { setExtras((x) => [...x, ...Array.from(e.target.files)].slice(0, 8)); e.target.value = ""; }} />
              <button type="button" onClick={() => extrasRef.current?.click()} data-testid="realisation-form-extras" className="text-xs font-semibold text-forest hover:text-pine">
                + Ajouter des photos supplémentaires ({extras.length}/8)
              </button>
              {extras.length > 0 && (
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {extras.map((f, i) => (
                    <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-sand">
                      <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                      <button type="button" onClick={() => setExtras(extras.filter((_, j) => j !== i))} aria-label="Retirer" className="absolute top-1 right-1 h-5 w-5 rounded-full bg-ink/70 text-white grid place-items-center"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="flex items-center gap-3 text-sm text-body2">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="realisation-form-published" />
              Publier sur le site
            </label>

            <button type="submit" disabled={saving} data-testid="realisation-form-submit" className="w-full rounded-full bg-forest text-cream font-semibold py-3 text-sm hover:bg-pine transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Envoi des photos…" : editing ? "Enregistrer" : "Publier la réalisation"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
